import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: API_URL ? `${API_URL}/api/v1` : '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 402 Payment Required, redirect to subscription page
    if (error.response?.status === 402) {
      if (error.response?.data?.detail === 'subscription_required') {
        window.location.href = '/subscription';
      }
      return Promise.reject(error);
    }

    // If 429 daily limit exceeded, surface a friendly message
    if (error.response?.status === 429 && error.response?.data?.detail === 'usage_limit_exceeded') {
      const msg = document.documentElement.lang === 'en'
        ? 'Daily usage limit reached. Please try again tomorrow.'
        : 'Limite d\'utilisation quotidienne atteinte. Réessayez demain.';
      window.dispatchEvent(new CustomEvent('vetai:usage-limit', { detail: msg }));
      return Promise.reject(error);
    }

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const refreshURL = API_URL ? `${API_URL}/api/v1/auth/refresh` : '/api/v1/auth/refresh';
        const response = await axios.post(refreshURL, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: new_refresh_token } = response.data;
        localStorage.setItem('access_token', access_token);
        if (new_refresh_token) localStorage.setItem('refresh_token', new_refresh_token);
        // Sync refreshed tokens to extension storage so they survive panel close/reopen
        if (window.parent !== window) {
          const payload: Record<string, string> = { access_token };
          if (new_refresh_token) payload.refresh_token = new_refresh_token;
          window.parent.postMessage({ type: 'VETAI_STORE_TOKENS', payload }, '*');
        }

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — wipe ALL auth state (tokens + vet info) so the route guard
        // sees an unauthenticated user and shows the login form cleanly.
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('current_vet_id');
        localStorage.removeItem('current_vet_name');
        localStorage.removeItem('auth_user');
        // Notify extension to purge chrome.storage.local as well
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'VETAI_CLEAR_TOKENS' }, '*');
        }
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
